/*
 * Bank payouts: pure response shaping, labels, the generate choices, form validation and error
 * routing, pinned to teacher_payouts.json by adapters.test.js.
 */
import { EM_DASH, api_error, fmt, fmt_paise as money, month_label } from '../format';
import { with_ist } from '../shared/ist';
import { reason_length } from '../needs_attention/params';
import {
  ACTION_FAILED,
  ACTION_IGNORE,
  ACTION_RECOVER,
  ACTION_SENT,
  KIND_NEW_ROUND,
  KIND_R1,
  KIND_REGENERATE
} from './requests';
import { BANK_DETAILS_FORBIDDEN_TEXT } from '../shared/bank_access';

const str = (v) => (typeof v === 'string' ? v : '');
const arr = (v) => (Array.isArray(v) ? v : []);
const shown = (v) => str(v) || EM_DASH;
// The wire number behind a formatted amount, for CSV export (null when missing).
const raw_num = (v) => (typeof v === 'number' ? v : null);
const cursor = (v) => (typeof v === 'number' && v > 0 ? v : null);

export const REASON_MIN_LEN = 5;
export const REASON_MAX_LEN = 1024;
export const BANK_REFERENCE_MAX = 64;
export const HOLIDAY_NAME_MAX = 128;
export const RESPONSE_MAX_BYTES = 2097152;

// Every 403 here is the payments-role gate; the server's wording is replaced by one clear line.
export const FORBIDDEN_TEXT = BANK_DETAILS_FORBIDDEN_TEXT;
export const PARTIAL_NEEDS_INVOICE_TEXT =
  'No confirmed invoice for this month, so paid outside must be the full outstanding amount.';
// Mirrors goserver PAYOUT_BANK_PAID_NEEDS_INVOICE: recording a partial amount now would lock out the invoice the rest needs.
export const BANK_PAID_NEEDS_INVOICE_TEXT =
  "Ask the teacher to confirm this month's invoice first, then record what the bank paid as paid outside; the next bank file pays the rest.";
export const BEFORE_CUTOVER_TEXT =
  'Months before the cutover month were paid outside this system, so they have no bank file.';

// ---- labels ----

const label_of = (map, code) => map[code] || { label: str(code) || EM_DASH, tone: 'gray' };

const ITEM_STATES = {
  IN_FILE: { label: 'In a bank file', tone: 'blue' },
  SENT: { label: 'Sent to bank', tone: 'green' },
  FAILED: { label: 'Did not go through', tone: 'red' },
  HELD: { label: 'Held for Ops', tone: 'orange' },
  CANCELLED: { label: 'Cancelled', tone: 'gray' },
  PAID_OUTSIDE: { label: 'Paid outside the file', tone: 'green' }
};
export const item_state_view = (code) => label_of(ITEM_STATES, code);

const MATCHES = {
  MATCHED_REFERENCE: { label: 'Matched by our reference', tone: 'green' },
  MATCHED_UTR: { label: 'Matched by bank reference', tone: 'green' },
  MATCHED_FALLBACK: { label: 'Matched by account and amount', tone: 'blue' },
  UNMATCHED: { label: 'No matching payment', tone: 'orange' },
  AMBIGUOUS: { label: 'Matches more than one payment', tone: 'orange' },
  NOT_OURS: { label: 'Not a teacher payment', tone: 'gray' },
  INVALID_LINE: { label: 'Line could not be read', tone: 'red' }
};
export const match_view = (code) => label_of(MATCHES, code);

const PROPOSED = {
  MARK_SENT: { label: 'Mark sent', tone: 'green' },
  MARK_FAILED: { label: 'Mark failed', tone: 'red' },
  NO_CHANGE: { label: 'No change (already recorded)', tone: 'gray' },
  IGNORE: { label: 'Ignore', tone: 'gray' },
  HOLD_UNKNOWN_STATUS: { label: 'Hold: bank status is not E or R', tone: 'orange' },
  HOLD_NO_UTR: { label: 'Hold: sent without a bank reference', tone: 'orange' },
  HOLD_AMOUNT_MISMATCH: { label: 'Hold: amount differs from our line', tone: 'orange' },
  HOLD_ACCOUNT_DIFFERS: { label: 'Hold: account differs from our line', tone: 'orange' },
  HOLD_CANCELLED_FILE: {
    label: 'Hold: line of a cancelled file (possible double payment)',
    tone: 'red'
  },
  HOLD_CONFLICT: { label: 'Hold: conflicts with an earlier bank answer', tone: 'orange' },
  HOLD_ITEM_HELD: { label: 'Hold: the payment is already held', tone: 'orange' },
  HOLD_PAID_OUTSIDE: { label: 'Hold: already paid outside the file', tone: 'red' },
  HOLD_DUPLICATE: { label: 'Hold: second line for the same payment', tone: 'orange' },
  HOLD_UNMATCHED: { label: 'Hold: no matching payment', tone: 'orange' },
  HOLD_AMBIGUOUS: { label: 'Hold: matches more than one payment', tone: 'orange' },
  HOLD_INVALID_LINE: { label: 'Hold: line could not be read', tone: 'red' },
  HOLD_BAD_UTR: {
    label: 'Hold: bank reference not valid — type the correct UTR to count as sent',
    tone: 'orange'
  }
};
export const proposed_view = (code) => label_of(PROPOSED, code);

// A list row's outcome: a short nowrap chip, the hold's reason moved to the row's note line.
export const outcome_chip = (proposed) => {
  const p = proposed || {};
  const label = str(p.label);
  const m = label.match(/^Hold: (.+)$/);
  if (!m) return { label, tone: p.tone, reason: '' };
  return { label: 'Held', tone: p.tone, reason: m[1].charAt(0).toUpperCase() + m[1].slice(1) };
};

const APPLIED = {
  PENDING: { label: 'Not applied yet', tone: 'blue' },
  APPLIED: { label: 'Applied', tone: 'green' },
  HELD: { label: 'Held for Ops', tone: 'orange' },
  IGNORED: { label: 'Ignored', tone: 'gray' },
  NO_CHANGE: { label: 'No change', tone: 'gray' },
  RESOLVED: { label: 'Resolved by Ops', tone: 'blue' }
};
export const applied_view = (code) => label_of(APPLIED, code);

export const OPS_ACTIONS = [
  { value: ACTION_SENT, label: 'Count as sent' },
  { value: ACTION_FAILED, label: 'Count as failed' },
  { value: ACTION_IGNORE, label: 'Ignore' },
  { value: ACTION_RECOVER, label: 'Recover from next month' }
];
const ops_action_label = (code) => {
  const hit = OPS_ACTIONS.find((a) => a.value === code);
  return hit ? hit.label : str(code);
};

// recovery is offered only on a line the server says it would accept (recover_pay_month set).
export const can_recover = (line) => !!line && !!str(line.recover_pay_month);

// The actions one held line offers, with the recovery named by its amount and month.
// The server refuses Count as sent when the bank paid another amount: Ops count it failed, then record what was paid outside.
export const held_amounts_differ = (line) => {
  const l = line || {};
  const bank = rupees_paise(l.amount_raw);
  const ours = rupees_paise(l.item_amount_raw);
  return bank !== null && ours !== null && bank !== ours;
};

// What Ops do instead of Count as sent on an amount mismatch ('' when the amounts agree or one is unknown).
export const amount_differs_text = (line) => {
  if (!held_amounts_differ(line)) return '';
  const bank = rupees_paise(line.amount_raw);
  const ours = rupees_paise(line.item_amount_raw);
  const head = `The bank paid ${line.amount}, but our line is ${line.item_amount}, so it can't be counted as sent.`;
  return bank < ours
    ? `${head} Count it as failed, then record ${line.amount} as paid outside in the teacher-month view. The rest stays owed.`
    : `${head} Count it as failed, then record ${line.item_amount} as paid outside in the teacher-month view. The extra ${paise_money(bank - ours)} is an overpayment: raise it with the payments lead.`;
};

export const bank_rejected = (line) => str((line || {}).status_code).toUpperCase() === 'R';
const cancelled_file = (line) => str((line || {}).proposed_action) === 'HOLD_CANCELLED_FILE';

// Why Count as sent is not offered on this line, for the dialog ('' when it is offered or Recover explains itself).
export const count_as_sent_blocked_text = (line) => {
  if (bank_rejected(line)) {
    return cancelled_file(line)
      ? 'The bank rejected this payment from a cancelled file, so it was not paid. Ignore this line.'
      : "The bank rejected this payment, so it was not paid and can't be counted as sent. Count it as failed.";
  }
  return amount_differs_text(line);
};

// The bank says it paid a payment that had failed: Ignore would record nothing, so the server refuses it.
export const ignore_blocked = (line) =>
  (line || {}).status_code === 'E' && (line || {}).item_state_code === 'FAILED';

export const ignore_blocked_text = (line) =>
  ignore_blocked(line)
    ? "The bank says it paid this payment, which had failed: count it as sent or as failed. It can't be ignored."
    : '';

export const AMOUNT_DIFFERS_CHECK_LABEL =
  'I checked the bank statement: the bank paid a different amount, and I will record what was paid as paid outside.';

export const ops_actions_for = (line) =>
  OPS_ACTIONS.filter(
    (a) =>
      (a.value !== ACTION_RECOVER || can_recover(line)) &&
      (a.value !== ACTION_IGNORE || !ignore_blocked(line)) &&
      (a.value !== ACTION_SENT ||
        // The server refuses these; a covered cancelled-file line is recovered, not counted again.
        !(
          held_amounts_differ(line) ||
          bank_rejected(line) ||
          (cancelled_file(line) && can_recover(line))
        ))
  ).map((a) =>
    a.value === ACTION_RECOVER
      ? {
          ...a,
          label: `Withhold ${line.amount} from the ${month_label(line.recover_pay_month)} payout`
        }
      : a
  );

// (after tax): the confirm button names the money and the payout it is withheld from.
export const recover_confirm_text = (line) =>
  `Yes, withhold ${line.amount} from the ${month_label(line.recover_pay_month)} payout`;

// The confirm banner: what is withheld from which payout, and what does not change.
export const recover_banner_text = (line) => {
  const month = month_label(line.recover_pay_month);
  return (
    `Count line ${line.line_no} as sent and withhold ${line.amount} from the teacher's ${month} payout? ` +
    `Their earnings, GST, TDS and invoice do not change. If the ${month} payout is smaller, it pays nothing and the ` +
    'rest is withheld from the next months. The teacher is not emailed about the duplicate. This cannot be undone here.'
  );
};

// a held line matched to a recovered duplicate still being withheld (null otherwise); amounts are
// paise.
export const recovery_effect_view = (e) => {
  // No warning beats a wrong figure: without the amount the effect is dropped.
  if (!e || typeof e !== 'object' || typeof e.amount_paise !== 'number') return null;
  const paise = (v) => (typeof v === 'number' ? v : 0);
  return {
    teacher_name: str(e.teacher_name),
    paid_month: str(e.paid_month),
    amount_paise: paise(e.amount_paise),
    withheld_from_months: arr(e.withheld_from_months).filter((m) => typeof m === 'string'),
    withheld_paise: paise(e.withheld_paise),
    outstanding_paise: paise(e.outstanding_paise)
  };
};

export const paise_money = (p) => fmt(p / 100, 2);

const join_and = (list) =>
  list.length <= 1 ? list[0] || '' : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;

// shown when "Count as failed" is chosen on a recovered duplicate: failing it stops the recovery on
// its own.
export const recovery_stop_warning = (line) => {
  const e = line && line.recovery_effect;
  if (!e) return '';
  const who = e.teacher_name ? `${e.teacher_name}'s` : "the teacher's";
  const amount = paise_money(e.amount_paise);
  const months = e.withheld_from_months;
  if (!months.length) {
    return (
      `This payment is set to be recovered from ${who} future payouts. ` +
      `Counting it as failed stops the recovery: ${amount} will no longer be held back.`
    );
  }
  return (
    `This payment is being recovered from ${who} ${join_and(months.map(month_label))} ` +
    `${months.length > 1 ? 'payouts' : 'payout'}. Counting it as failed stops the recovery: ${amount} is no ` +
    'longer held back, and any amount already held back becomes payable again.'
  );
};

// the server could not check this line for a recovery (it still re-checks on Count as failed).
export const RECOVERY_UNKNOWN_TEXT =
  'Could not check whether this payment is being recovered. Reload before counting it as failed.';

const TEACHER_STATES = {
  BEFORE_CUTOVER: { label: 'Paid outside this system', tone: 'gray' },
  NOT_CLOSED: { label: 'Month not closed', tone: 'gray' },
  NOTHING_TO_PAY: { label: 'Nothing to pay', tone: 'gray' },
  PAYOUT_ON_HOLD_INVOICE: { label: 'On hold: invoice', tone: 'orange' },
  PAYOUT_ON_HOLD_DETAILS: { label: 'On hold: payment details', tone: 'orange' },
  CONFIRMED_NEXT_PAYMENT: { label: 'In the next payment', tone: 'blue' },
  PROCESSING: { label: 'Processing', tone: 'blue' },
  SENT: { label: 'Sent to bank', tone: 'green' },
  FAILED: { label: 'Did not go through', tone: 'red' },
  PAID_SEPARATELY: { label: 'Paid separately', tone: 'green' },
  RECOVERY_WITHHELD: { label: 'Withheld: duplicate payment recovery', tone: 'orange' }
};
export const teacher_state_view = (code) => label_of(TEACHER_STATES, code);

const FILE_STATUSES = {
  ACTIVE: { label: 'Active', tone: 'green' },
  CANCELLED: { label: 'Cancelled', tone: 'gray' }
};
export const file_status_view = (code) => label_of(FILE_STATUSES, code);

export const CLAIM_STATUSES = [
  { value: 'OPEN', label: 'Open' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'ALL', label: 'All' }
];
export const CLAIM_STATUS_DEFAULT = 'OPEN';
export const claims_filters_on = (status) => !!status && status !== CLAIM_STATUS_DEFAULT;

const CLAIM_STATUS_WORDS = {
  OPEN: 'open only',
  RESOLVED: 'resolved only',
  ALL: 'open and resolved'
};

// The line under the report filters; null until the list has loaded. A cursor list counts what it shows.
export const claims_filter_summary = (status, list) => {
  if (!list || list.status !== 'ready') return null;
  const n = list.rows.length;
  const count = `${n}${list.next ? '+' : ''}`;
  const noun = n === 1 && !list.next ? 'report' : 'reports';
  return {
    lead: `${count} ${noun}`,
    rest: `: ${CLAIM_STATUS_WORDS[status] || CLAIM_STATUS_WORDS.OPEN}.`
  };
};
const CLAIM_TONES = {
  OPEN: { label: 'Open', tone: 'orange' },
  RESOLVED: { label: 'Resolved', tone: 'green' }
};
export const claim_status_view = (code) => label_of(CLAIM_TONES, code);

// File column A: I = HDFC to HDFC, N = NEFT to another bank.
const MODES = { I: 'HDFC transfer', N: 'NEFT' };

const teacher_name = (v) => str(v) || 'Name not available';

// ---- eligibility and files ----

const file_view = (f) => {
  const d = f || {};
  const counts = d.line_counts && typeof d.line_counts === 'object' ? d.line_counts : {};
  return {
    uuid: str(d.uuid),
    round_no: typeof d.round_no === 'number' ? d.round_no : 0,
    kind: str(d.kind),
    file_name: str(d.file_name),
    status: str(d.status),
    status_view: file_status_view(d.status),
    payment_date: shown(d.payment_date),
    row_count: typeof d.row_count === 'number' ? d.row_count : 0,
    total: money(d.total),
    total_raw: raw_num(d.total),
    generated_by: shown(d.generated_by_name),
    generated_at: with_ist(shown(d.generated_at_ist)),
    replaces_file_uuid: str(d.replaces_file_uuid),
    cancelled_at: with_ist(str(d.cancelled_at_ist)),
    cancel_reason: str(d.cancel_reason),
    line_counts: counts,
    counts_text: Object.keys(counts)
      .sort()
      .map((k) => `${counts[k]} ${item_state_view(k).label.toLowerCase()}`)
      .join(', '),
    downloads: arr(d.downloads).map((x) => ({
      by: shown(x && x.by),
      at: with_ist(shown(x && x.at_ist))
    })),
    // The bank has answered some line: regenerating would cancel lines it may also pay.
    answered: Object.keys(counts).some((k) => k !== 'IN_FILE' && k !== 'CANCELLED' && counts[k] > 0)
  };
};

// Left-out reasons that get a pill and a row highlight; any other code shows the server's sentence only.
export const LEFT_OVER_BANK_LINE_LIMIT = 'OVER_BANK_LINE_LIMIT';
const LEFT_REASONS = {
  [LEFT_OVER_BANK_LINE_LIMIT]: { label: 'Over ₹3,00,000: pay outside the file', tone: 'orange' },
  // Ops must record what the bank paid before the month can go in a file again.
  BANK_PAID_OTHER_AMOUNT: { label: 'Bank paid another amount: record paid outside', tone: 'orange' }
};

const left_out_view = (r) => {
  const reason = str(r && r.reason);
  const reason_text = str(r && r.reason_text) || reason || EM_DASH;
  return {
    teacher_uuid: str(r && r.teacher_uuid),
    teacher_name: teacher_name(r && r.teacher_name),
    reason,
    reason_text,
    reason_view: LEFT_REASONS[reason] || { label: reason_text, tone: 'gray' },
    flagged: Boolean(LEFT_REASONS[reason]),
    outstanding: r && typeof r.outstanding === 'number' ? money(r.outstanding) : EM_DASH,
    outstanding_raw: raw_num(r && r.outstanding)
  };
};

// the Bank file banner for lines the bank file cannot carry (when none).
export const over_limit_text = (n) =>
  n > 0
    ? `${n} ${n === 1 ? 'payment' : 'payments'} over ₹3,00,000 ${n === 1 ? 'needs' : 'need'} paying outside the file.`
    : '';

// Warning-only row flags; they never change selection or the generate request.
const FLAG_LARGE = 'LARGE_AMOUNT';
const FLAG_ABOVE_AVERAGE = 'ABOVE_AVERAGE';
const FLAG_TONES = { [FLAG_LARGE]: 'orange', [FLAG_ABOVE_AVERAGE]: 'yellow' };

// three_month_average is a rupees value (number or decimal string) or null.
const average = (v) => {
  const shown_avg = typeof v === 'number' || typeof v === 'string' ? fmt(v, 2) : EM_DASH;
  return shown_avg === EM_DASH ? null : shown_avg;
};

const flag_label = (code, avg) => {
  if (code === FLAG_LARGE) return 'Large amount';
  if (code === FLAG_ABOVE_AVERAGE)
    return avg ? `Above 3-month average (${avg})` : 'Above 3-month average';
  return str(code) || EM_DASH;
};

const flag_view = (code, text, avg) => ({
  code,
  label: flag_label(code, avg),
  text,
  tone: FLAG_TONES[code] || 'orange'
});

// Prefer the server's flags; an older server only sends large_amount.
const row_flags = (r, avg) => {
  if (!Array.isArray(r.flags))
    return r.large_amount === true ? [flag_view(FLAG_LARGE, '', avg)] : [];
  return r.flags
    .filter((f) => f && typeof f === 'object')
    .map((f) => flag_view(str(f.code), str(f.text), avg));
};

const has_flag = (code) => (r) => r.flags.some((f) => f.code === code);

export const eligibility_view = (data) => {
  const d = data || {};
  const eligible = arr(d.eligible).map((r) => {
    const avg = average(r.three_month_average);
    return {
      teacher_uuid: str(r.teacher_uuid),
      teacher_name: teacher_name(r.teacher_name),
      amount: money(r.amount),
      amount_raw: raw_num(r.amount),
      mode: MODES[r.mode] || shown(r.mode),
      bank_name: shown(r.bank_name),
      branch: shown(r.branch),
      account_number_masked: shown(r.account_number_masked),
      large_amount: r.large_amount === true,
      retry: r.retry === true,
      three_month_average: avg,
      flags: row_flags(r, avg),
      // kept back from amount to recover a duplicate payment (= nothing withheld).
      withheld: typeof r.withheld === 'number' && r.withheld > 0 ? money(r.withheld) : '',
      withheld_raw: raw_num(r.withheld)
    };
  });
  const left_out = arr(d.left_out).map(left_out_view);
  return {
    pay_month: str(d.pay_month),
    cutover_month: str(d.cutover_month),
    next_round: typeof d.next_round === 'number' ? d.next_round : 1,
    active_files: arr(d.active_files).map(file_view),
    eligible,
    eligible_total: money(d.eligible_total),
    large_count: eligible.filter((r) => r.large_amount || has_flag(FLAG_LARGE)(r)).length,
    above_average_count: eligible.filter(has_flag(FLAG_ABOVE_AVERAGE)).length,
    retry_count: eligible.filter((r) => r.retry).length,
    left_out,
    over_limit_count: left_out.filter((r) => r.reason === LEFT_OVER_BANK_LINE_LIMIT).length
  };
};

export const files_view = (data) => arr(data && data.files).map(file_view);

export const generate_view = (data) => {
  const d = data || {};
  return {
    file: file_view(d.file),
    left_out: arr(d.left_out).map(left_out_view),
    cancelled_lines: typeof d.cancelled_lines === 'number' ? d.cancelled_lines : 0,
    replay: d.replay === true
  };
};

/*
 * What Generate may do for the month. Otherwise a new round, plus a regenerate of each active
 * round the bank has not answered (an answered one is listed, disabled, with why).
 */
export const generate_choices = (eligibility, files) => {
  const all = arr(files);
  if (all.length === 0) {
    return [
      {
        key: 'R1',
        kind: KIND_R1,
        round_no: 1,
        label: 'Generate R1',
        file: null,
        file_uuid: '',
        disabled_text: ''
      }
    ];
  }
  const next =
    (eligibility && eligibility.next_round) || 1 + Math.max(...all.map((f) => f.round_no));
  const regen = all
    .filter((f) => f.status === 'ACTIVE')
    .map((f) => ({
      key: `REGEN-${f.uuid}`,
      kind: KIND_REGENERATE,
      round_no: f.round_no,
      label: `Regenerate R${f.round_no}`,
      file: f,
      file_uuid: f.uuid,
      disabled_text: f.answered
        ? 'The bank has already answered lines of this file, so it was uploaded: use a new round instead.'
        : ''
    }));
  return regen.concat([
    {
      key: 'NEW',
      kind: KIND_NEW_ROUND,
      round_no: next,
      label: `New round R${next}`,
      file: null,
      file_uuid: '',
      disabled_text: ''
    }
  ]);
};

// the month Ops pick must be the screen's, so a file is never made for the month next to the one
// they meant.
export const confirm_month_ok = (picked, pay_month) =>
  !!pay_month && str(picked).trim() === pay_month;

// the generate dialog's months: the cutover month on, newest first; the screen's month is always
// one.
export const generate_month_options = (months, cutover_month, pay_month) => {
  const cutover = str(cutover_month);
  const by_month = new Map();
  arr(months).forEach((m) => {
    const pm = m && str(m.pay_month);
    if (pm && (!cutover || pm >= cutover) && !by_month.has(pm)) {
      by_month.set(pm, str(m.label) || month_label(pm));
    }
  });
  if (pay_month && !by_month.has(pay_month)) by_month.set(pay_month, month_label(pay_month));
  return [...by_month.entries()]
    .sort(([a], [b]) => (a < b ? 1 : -1))
    .map(([value, label]) => ({ value, label }));
};

export const generate_month_mismatch_text = (picked, pay_month) =>
  !picked || picked === pay_month
    ? ''
    : `This screen shows ${month_label(pay_month)}. To make a file for ${month_label(picked)}, ` +
      'pick it in Payment month at the top of the page.';

export const regenerate_warning = (file) => {
  const f = file || {};
  const n = (f.line_counts && f.line_counts.IN_FILE) || 0;
  return (
    `Make sure R${f.round_no} (${f.file_name || 'the earlier file'}) was NOT uploaded to the bank. ` +
    `Regenerating cancels its ${n} unpaid ${n === 1 ? 'line' : 'lines'} and makes a new file; if the bank ` +
    'also pays the earlier file, those teachers are paid twice.'
  );
};

export const downloads_text = (file) => {
  const list = arr(file && file.downloads);
  if (list.length === 0) return 'Never downloaded.';
  return `Downloaded ${list.length === 1 ? 'once' : `${list.length} times`}: ${list
    .map((x) => `${x.by} at ${x.at}`)
    .join('; ')}.`;
};

export const new_round_note = (round_no) =>
  `R${round_no} pays only teachers who are not already in a file waiting for the bank's answer. ` +
  'Upload it to the bank as a separate file.';

export const generate_done_text = (view) => {
  const v = view || {};
  const f = v.file || {};
  if (v.replay)
    return `This file was already made: ${f.file_name}. Download it from the files list.`;
  const cancelled = v.cancelled_lines
    ? ` ${v.cancelled_lines} ${v.cancelled_lines === 1 ? 'line' : 'lines'} of the earlier file cancelled.`
    : '';
  return `Bank file R${f.round_no} made: ${f.row_count} ${f.row_count === 1 ? 'teacher' : 'teachers'}, ${f.total}.${cancelled}`;
};

const SAFE_FILE_NAME_RE = /^[A-Za-z0-9._-]{1,200}$/;

// The server's own file name when it is a plain one, else a neutral fallback (never steers the save path).
export const bank_file_name = (file, header_name = '') => {
  if (SAFE_FILE_NAME_RE.test(header_name)) return header_name;
  const name = str(file && file.file_name);
  if (SAFE_FILE_NAME_RE.test(name)) return name;
  return `bank_file_R${(file && file.round_no) || 0}.csv`;
};

// ---- bank response ----

const line_view = (l) => {
  const d = l || {};
  return {
    line_id: typeof d.line_id === 'number' ? d.line_id : 0,
    line_no: typeof d.line_no === 'number' ? d.line_no : 0,
    status_code: shown(d.status_code),
    beneficiary_name: shown(d.beneficiary_name),
    account_last4: str(d.account_last4),
    ifsc: shown(d.ifsc),
    amount: money(d.amount),
    amount_raw: typeof d.amount === 'number' ? d.amount : null,
    value_date: str(d.value_date),
    bank_reference: str(d.bank_reference),
    bank_reason: str(d.bank_reason),
    match: match_view(d.match_result),
    proposed: proposed_view(d.proposed_action),
    proposed_action: str(d.proposed_action),
    applied: applied_view(d.applied_state),
    applied_state: str(d.applied_state),
    ops_action: d.ops_action ? ops_action_label(d.ops_action) : '',
    action_note: str(d.action_note),
    teacher_uuid: str(d.teacher_uuid),
    teacher_name: str(d.teacher_name),
    pay_month: str(d.pay_month),
    payout_item_uuid: str(d.payout_item_uuid),
    item_state: d.item_state ? item_state_view(d.item_state) : null,
    item_state_code: str(d.item_state),
    item_amount: money(d.item_amount),
    item_amount_raw: typeof d.item_amount === 'number' ? d.item_amount : null,
    response_uuid: str(d.response_uuid),
    recover_pay_month: str(d.recover_pay_month),
    recovery_effect: recovery_effect_view(d.recovery_effect),
    recovery_effect_unavailable: d.recovery_effect_unavailable === true
  };
};

export const held_view = (data) => ({
  rows: arr(data && data.rows).map(line_view),
  next_after_id: cursor(data && data.next_after_id)
});

export const response_view = (data) => {
  const d = data || {};
  const sum = d.summary || {};
  const by_action = sum.by_action && typeof sum.by_action === 'object' ? sum.by_action : {};
  const count = (k) => by_action[k] || 0;
  const held = Object.keys(by_action)
    .filter((k) => k.indexOf('HOLD_') === 0)
    .reduce((n, k) => n + by_action[k], 0);
  return {
    response_uuid: str(d.response_uuid),
    pay_month: str(d.pay_month),
    file_name: shown(d.file_name),
    status: str(d.status),
    applied: str(d.status) === 'APPLIED',
    uploaded_by: shown(d.uploaded_by_name),
    uploaded_at: with_ist(shown(d.uploaded_at_ist)),
    applied_at: with_ist(str(d.applied_at_ist)),
    already_uploaded: d.already_uploaded === true,
    already_applied: d.already_applied === true,
    lines: arr(d.lines).map(line_view),
    summary: {
      lines: sum.lines || 0,
      sent: count('MARK_SENT'),
      failed: count('MARK_FAILED'),
      no_change: count('NO_CHANGE'),
      ignored: count('IGNORE'),
      held,
      sent_total: money(sum.sent_total),
      failed_total: money(sum.failed_total),
      double_payment_alerts: sum.double_payment_alerts || 0
    }
  };
};

export const apply_confirm_text = (view) => {
  const s = (view && view.summary) || {};
  return (
    `Apply this bank response? ${s.sent} marked sent (${s.sent_total}), ${s.failed} marked failed ` +
    `(${s.failed_total}), ${s.held} held for Ops, ${s.ignored} ignored, ${s.no_change} unchanged. ` +
    'Teachers are told about sent and failed payments.'
  );
};

export const response_file_error = (file) => {
  if (!file) return 'Choose the bank response file.';
  if (file.size > RESPONSE_MAX_BYTES) return 'The file is larger than 2 MB: is it the right file?';
  if (file.size === 0) return 'The file is empty.';
  return '';
};

// ---- teacher-month drill-down ----

const item_view = (it) => {
  const d = it || {};
  return {
    uuid: str(d.uuid),
    state: str(d.state),
    state_view: item_state_view(d.state),
    amount: money(d.amount),
    round_no: typeof d.round_no === 'number' ? `R${d.round_no}` : 'Outside file',
    reference: shown(d.reference),
    close_version_no: d.close_version_no,
    sent_on: shown(d.sent_on),
    bank_reference: shown(d.bank_reference),
    why: str(d.bank_reason) || str(d.held_reason),
    account: [str(d.bank_name), str(d.account_number_masked)].filter(Boolean).join(' ') || EM_DASH,
    bank_name: str(d.bank_name),
    // Only a line paid to an account has a branch to show (a payment recorded outside has neither).
    branch_line: str(d.account_number_masked) ? `Branch: ${shown(d.branch)}` : '',
    account_masked: str(d.account_number_masked),
    amount_raw: typeof d.amount === 'number' ? d.amount : null,
    retry_allowed: d.retry_allowed === true,
    resolution_note: str(d.resolution_note),
    created_at: with_ist(shown(d.created_at_ist))
  };
};

const claim_view = (c) => {
  const d = c || {};
  return {
    claim_uuid: str(d.claim_uuid),
    status: str(d.status),
    status_view: claim_status_view(d.status),
    teacher_uuid: str(d.teacher_uuid),
    teacher_name: teacher_name(d.teacher_name),
    pay_month: shown(d.pay_month),
    pay_month_raw: str(d.pay_month),
    payout_item_uuid: str(d.payout_item_uuid),
    amount: money(d.amount),
    amount_raw: raw_num(d.amount),
    sent_on: shown(d.sent_on),
    bank_reference: shown(d.bank_reference),
    note: str(d.note),
    created_at: with_ist(shown(d.created_at_ist)),
    resolved_by: str(d.resolved_by),
    resolved_at: with_ist(str(d.resolved_at_ist)),
    resolution_note: str(d.resolution_note)
  };
};

export const teacher_month_view = (data) => {
  const d = data || {};
  const tv = d.teacher_view || {};
  const elig = d.eligibility || {};
  const outstanding = typeof d.outstanding === 'number' ? d.outstanding : null;
  return {
    teacher_uuid: str(d.teacher_uuid),
    teacher_name: teacher_name(d.teacher_name),
    pay_month: str(d.pay_month),
    lifecycle: str(d.lifecycle),
    payable: money(d.payable),
    paid_so_far: money(d.paid_so_far),
    outstanding: money(outstanding),
    outstanding_raw: outstanding,
    // withheld from this month's payout to recover duplicate payments, one line each.
    withheld: typeof d.withheld === 'number' && d.withheld > 0 ? money(d.withheld) : '',
    recoveries: arr(d.recoveries).map((x) => ({
      label: str(x && x.label),
      amount: money(x && x.amount)
    })),
    eligibility_text: str(elig.reason)
      ? str(elig.reason_text) || str(elig.reason)
      : 'The next bank file pays this month.',
    teacher_headline: shown(tv.headline),
    teacher_state: teacher_state_view(tv.state),
    items: arr(d.items).map(item_view),
    claims: arr(d.claims).map(claim_view),
    // only a closed month with money still owed can be settled outside the file.
    can_pay_outside: str(d.lifecycle) === 'CLOSED' && outstanding !== null && outstanding > 0,
    // Only an explicit false: an older server omits the flag, and then the 409 path decides.
    outside_full_only: d.has_confirmed_invoice === false,
    // What the bank paid on a line counted as failed; less than owed = record it (after the invoice), more = record the full outstanding.
    bank_paid_other_raw:
      typeof d.bank_paid_other_amount === 'number' ? d.bank_paid_other_amount : null,
    bank_paid_less:
      typeof d.bank_paid_other_amount === 'number' &&
      outstanding !== null &&
      d.bank_paid_other_amount < outstanding,
    bank_paid_other:
      typeof d.bank_paid_other_amount === 'number' ? money(d.bank_paid_other_amount) : ''
  };
};

export const claims_view = (data) => ({
  rows: arr(data && data.rows).map(claim_view),
  next_after_id: cursor(data && data.next_after_id)
});

const deleted_label = (r) => {
  const id = typeof r.teacher_id === 'number' ? `Teacher #${r.teacher_id}` : str(r.teacher_uuid);
  const name = str(r.teacher_name);
  return name && id ? `${name} (${id})` : name || id;
};

export const deleted_view = (data) => ({
  rows: arr(data && data.rows).map((r) => ({
    teacher_uuid: str(r.teacher_uuid),
    // The name is the server's placeholder for a deleted account; the id is what Ops search by.
    teacher_label: deleted_label(r),
    teacher_id: raw_num(r.teacher_id),
    teacher_name: str(r.teacher_name),
    total: money(r.total),
    total_raw: raw_num(r.total),
    account:
      [str(r.approved_bank_name), str(r.account_number_masked)].filter(Boolean).join(' ') ||
      'No approved details',
    branch_line: str(r.account_number_masked) ? `Branch: ${shown(r.approved_branch)}` : '',
    months: arr(r.months).map((m) => ({
      pay_month: str(m && m.pay_month),
      lifecycle: str(m && m.lifecycle),
      outstanding: money(m && m.outstanding),
      outstanding_raw: raw_num(m && m.outstanding)
    }))
  })),
  next_after_teacher_id: cursor(data && data.next_after_teacher_id)
});

export const holidays_view = (data) =>
  arr(data && data.holidays)
    .map((h) => ({ date: str(h && h.date), name: shown(h && h.name) }))
    .sort((a, b) => (a.date < b.date ? -1 : 1));

// This IST year and the next: holidays are entered ahead, never years back.
export const holiday_years = (today) => {
  const y = Number(str(today).slice(0, 4)) || new Date().getUTCFullYear();
  return [y, y + 1];
};

// ---- validation ----

const AMOUNT_RE = /^\d{1,10}(\.\d{1,2})?$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const REFERENCE_RE = /^[A-Za-z0-9/_.-]{1,64}$/;

// Rupee text to whole paise without floating point; null when it is not a bank amount.
export const amount_paise = (raw) => {
  const v = str(raw).trim();
  if (!AMOUNT_RE.test(v)) return null;
  const [whole, frac = ''] = v.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
};

// A wire amount (rupees with paise) as whole paise.
export const rupees_paise = (n) => (typeof n === 'number' ? Math.round(n * 100) : null);

const real_date = (v) => {
  if (!DATE_RE.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
};

export const past_date_error = (raw, today) => {
  const v = str(raw).trim();
  if (!v) return 'Enter the date.';
  if (!real_date(v) || v > today) return 'Enter the date as YYYY-MM-DD, not in the future.';
  return '';
};

export const reference_error = (raw, required = true) => {
  const v = str(raw).trim();
  if (!v) return required ? 'Enter the payment reference.' : '';
  return REFERENCE_RE.test(v) ? '' : 'Use letters, digits and / _ . - only, up to 64 characters.';
};

export const payout_reason_error = (raw, what = 'reason') => {
  const n = reason_length(raw);
  if (n === 0) return `Write the ${what}.`;
  if (n < REASON_MIN_LEN) return `The ${what} must be at least ${REASON_MIN_LEN} characters.`;
  if (n > REASON_MAX_LEN) return `The ${what} must be at most ${REASON_MAX_LEN} characters.`;
  return '';
};

// Whole-paise wire rupees as the form's amount text ("2500.00"), so a locked full amount matches exactly.
export const full_amount_text = (outstanding) => {
  const p = rupees_paise(outstanding);
  if (p === null || p < 0) return '';
  return `${Math.floor(p / 100)}.${String(p % 100).padStart(2, '0')}`;
};

// form: {amount, paid_on, reference, reason}; outstanding: the month's wire value (rupees).
// full_only: the server said no confirmed invoice, so only the whole outstanding may be recorded.
export const paid_outside_errors = (
  form,
  outstanding,
  today,
  full_only = false,
  bank_paid = null
) => {
  const f = form || {};
  const paise = amount_paise(f.amount);
  const cap = rupees_paise(outstanding);
  const bank_paise = rupees_paise(bank_paid);
  let amount = '';
  if (!str(f.amount).trim()) amount = 'Enter the amount.';
  else if (paise === null || paise <= 0)
    amount = 'Enter the amount in rupees with at most 2 decimals, above zero.';
  else if (cap !== null && paise > cap)
    amount = `The amount is more than this month still owes (${fmt(outstanding, 2)}).`;
  else if (full_only && cap !== null && paise !== cap)
    amount = bank_paise !== null ? BANK_PAID_NEEDS_INVOICE_TEXT : PARTIAL_NEEDS_INVOICE_TEXT;
  return {
    amount,
    paid_on: past_date_error(f.paid_on, today),
    reference: reference_error(f.reference),
    reason: payout_reason_error(f.reason)
  };
};

// The stored copy of a bad UTR may be cut, so the server refuses it: Ops must type the reference.
export const bad_utr = (line) => !!line && line.proposed_action === 'HOLD_BAD_UTR';

// line: a held line_view; form: {action, reason, bank_reference, sent_on, payout_item_uuid}.
export const resolve_errors = (line, form, today) => {
  const l = line || {};
  const f = form || {};
  const out = {
    action: '',
    reason: payout_reason_error(f.reason),
    bank_reference: '',
    sent_on: '',
    payout_item_uuid: ''
  };
  if (!ops_actions_for(l).some((a) => a.value === f.action))
    out.action = 'Choose what to do with this line.';
  if (f.action === ACTION_SENT || f.action === ACTION_RECOVER) {
    out.bank_reference = reference_error(f.bank_reference, !l.bank_reference || bad_utr(l));
    out.sent_on = str(f.sent_on).trim() || !l.value_date ? past_date_error(f.sent_on, today) : '';
  }
  if ((f.action === ACTION_SENT || f.action === ACTION_FAILED) && !l.payout_item_uuid) {
    out.payout_item_uuid = str(f.payout_item_uuid).trim()
      ? ''
      : 'This line matched no payment: enter the payment ID it answers (from the teacher-month view).';
  }
  return out;
};

export const holiday_errors = (date, name) => ({
  date:
    DATE_RE.test(str(date).trim()) && real_date(str(date).trim())
      ? ''
      : 'Enter the date as YYYY-MM-DD.',
  name: (() => {
    const n = Array.from(str(name).trim()).length;
    if (n === 0) return 'Enter the holiday name.';
    return n > HOLIDAY_NAME_MAX ? `The name must be at most ${HOLIDAY_NAME_MAX} characters.` : '';
  })()
});

export const has_error = (errors) => Object.values(errors || {}).some(Boolean);

// ---- errors ----

const STALE_CODES = [
  'PAYOUT_LINE_NOT_HELD',
  'PAYOUT_CLAIM_ALREADY_RESOLVED',
  'PAYOUT_FILE_NOT_ACTIVE',
  'PAYOUT_FILE_ALREADY_ANSWERED',
  'PAYOUT_ELIGIBILITY_CHANGED',
  'PAYOUT_ITEM_STATE_CONFLICT',
  'PAYOUT_RETRY_NOT_LATEST',
  'PAYOUT_CHOOSE_REGENERATE_OR_NEW_ROUND',
  'PAYOUT_NO_FILE_YET',
  'PAYOUT_RECOVERY_MONTH_CHANGED',
  'PAYOUT_RECOVERY_NO_OPEN_MONTH',
  'PAYOUT_RECOVERY_NOT_DOUBLE_PAYMENT',
  'PAYOUT_HELD_CHANGED'
];

const DROPPED_TEXT = 'No answer from the server. It may have been saved: try again.';

export const payout_error = (e, fallback) => {
  const { code, message } = api_error(e);
  const status = e && e.response && e.response.status;
  const text = message || fallback;
  if (!e || !e.response) return { kind: 'dropped', code: '', text: DROPPED_TEXT };
  if (status === 401) return { kind: 'auth', code, text };
  if (status === 403 || code === 'FORBIDDEN')
    return {
      kind: 'forbidden',
      code,
      text: status === 403 ? FORBIDDEN_TEXT : message || FORBIDDEN_TEXT
    };
  if (code === 'PAYOUT_PREVIEW_CHANGED') {
    // The 409 carries the refreshed ResponseView in data.
    const fresh = e.response.data && e.response.data.data;
    return { kind: 'preview_changed', code, text, fresh: fresh || null };
  }
  if (code === 'PAYOUT_CONFIRMATION_REQUIRED') {
    // Marking a recovered duplicate as failed needs confirmation; data carries the recovery Ops must confirm.
    const body = e.response.data && e.response.data.data;
    const effect = recovery_effect_view(body && body.recovery_effect);
    if (effect) return { kind: 'recovery_stop', code, text, effect };
  }
  if (code === 'PAYOUT_PARTIAL_NEEDS_INVOICE')
    return { kind: 'full_only', code, text: PARTIAL_NEEDS_INVOICE_TEXT };
  if (code === 'PAYOUT_BEFORE_CUTOVER')
    return { kind: 'before_cutover', code, text: message || BEFORE_CUTOVER_TEXT };
  if (STALE_CODES.indexOf(code) !== -1) return { kind: 'stale', code, text };
  return { kind: 'inline', code, text };
};

/*
 * A failed money write. Any 5xx may have committed before the error, so it keeps the
 * client_request_id (keep_id) and a retry replays it; a gateway 5xx with no error code reads as
 * dropped. Only a 2xx or 4xx retires the id.
 */
export const write_error = (e, fallback) => {
  const err = payout_error(e, fallback);
  const status = (e && e.response && e.response.status) || 0;
  if (status < 500) return err;
  if (!err.code) return { kind: 'dropped', code: '', text: DROPPED_TEXT, keep_id: true };
  return { ...err, keep_id: true };
};

export const keeps_request_id = (err) => err.kind === 'dropped' || err.keep_id === true;

export const resolve_done_text = (data, action) => {
  const rec = data && data.recovery;
  if (action === ACTION_RECOVER && rec) {
    const what = `${money(rec.amount)} (${str(rec.label)}) from the ${month_label(rec.pay_month)} payout`;
    const later =
      typeof rec.unallocated === 'number' && rec.unallocated > 0
        ? ` ${money(rec.unallocated)} will be withheld from future payouts.`
        : '';
    return rec.replayed
      ? `Already recovered: withholding ${what}.${later}`
      : `Line counted as sent; withholding ${what}.${later}`;
  }
  const over = data && typeof data.overpaid === 'number' && data.overpaid > 0 ? data.overpaid : 0;
  const base = `Line resolved: ${ops_action_label(action).toLowerCase()}.`;
  return over
    ? `${base} This teacher-month is now paid ${fmt(over, 2)} more than it owes: recover it.`
    : base;
};

// ---- Ops confirmation step ----

// What each action's step 2 asks for; mirrors goserver payout.ConfirmationFor (the server re-checks it).
export const ACTION_RETRY = 'RETRY_SAME_DETAILS';
export const ACTION_PAID_OUTSIDE = 'PAID_OUTSIDE';
export const confirm_needs = (action) => {
  switch (action) {
    case ACTION_PAID_OUTSIDE:
      return { step: true, amount: true, checked: false };
    case ACTION_SENT:
    case ACTION_RECOVER:
      return { step: true, amount: true, checked: false };
    case ACTION_RETRY:
      return { step: true, amount: true, checked: true };
    case ACTION_FAILED:
      return { step: true, amount: false, checked: true };
    default:
      return { step: false, amount: false, checked: false };
  }
};

export const CHECK_LABELS = {
  [ACTION_RETRY]: 'I checked the bank statement: the earlier payment was not credited.',
  [ACTION_FAILED]: 'I checked that the bank returned this payment.'
};

// Typed rupees to whole paise: up to 2 decimals, commas, spaces and a leading ₹ allowed; null otherwise.
export const typed_amount_paise = (raw) => amount_paise(str(raw).replace(/[,\s₹]/g, ''));

// The amount Ops retype for a held line: the bank line's, else ours (goserver payout.HeldConfirmAmount, same rule).
export const held_confirm_paise = (line) => {
  const l = line || {};
  const bank = rupees_paise(l.amount_raw);
  return bank !== null ? bank : rupees_paise(l.item_amount_raw);
};

// '' when the step's inputs are complete and right, else what is missing (shown under the inputs).
export const confirm_error = (needs, input, expected_paise) => {
  const i = input || {};
  if (needs.amount) {
    if (!str(i.amount).trim()) return 'Type the amount to confirm.';
    const p = typed_amount_paise(i.amount);
    if (p === null) return 'Type the amount in rupees, with at most 2 decimals.';
    if (p !== expected_paise) return 'The amount does not match. Check it and type it again.';
  }
  if (needs.checked && i.checked !== true) return 'Tick the check box to confirm.';
  return '';
};

// The request fields of the step: confirm_amount_paise for typed amounts, confirmed for the check box.
export const confirm_fields = (needs, input) => {
  const i = input || {};
  return {
    confirm_amount_paise: needs.amount ? typed_amount_paise(i.amount) : null,
    confirmed: needs.checked ? i.checked === true : false
  };
};

const possessive = (name) => (name ? `${name}'s` : "the teacher's");
const name_or = (name) => name || 'The teacher';

// Step 2 copy for a held line: one or more plain sentences.
export const held_confirm_summary = (line, action) => {
  const l = line || {};
  if (action === ACTION_SENT) {
    // A differing bank amount never reaches here: Count as sent is not offered (held_amounts_differ).
    return [
      `Mark ${possessive(l.teacher_name)} ${month_label(l.pay_month)} payment as paid: our line ${l.item_amount}, ` +
        `bank line ${l.amount}. ${name_or(l.teacher_name)} gets the payment-sent email.`
    ];
  }
  if (action === ACTION_RECOVER) {
    return [
      `Hold back ${l.amount} from ${possessive(l.teacher_name)} next payouts, starting ${month_label(l.recover_pay_month)}.`,
      'Earnings, GST, TDS and the invoice do not change. The teacher is not emailed.'
    ];
  }
  if (action === ACTION_FAILED) {
    const warn = recovery_stop_warning(l);
    if (warn) return [warn];
    const out = [`Mark line ${l.line_no} (${l.amount}) as not paid.`];
    // A rejected line paid nothing, so there is no bank amount to record.
    const differs = bank_rejected(l) ? '' : amount_differs_text(l);
    if (differs) out.push(differs);
    if (l.recovery_effect_unavailable) out.push(RECOVERY_UNKNOWN_TEXT);
    return out;
  }
  return [];
};

// Last 4 digits of a masked account ("•••• 5544" -> "••••5544"); never more.
export const masked_last4 = (masked) => {
  const digits = str(masked).replace(/\D/g, '');
  return digits ? `••••${digits.slice(-4)}` : '';
};

// Step 2 copy for "Retry with same details" on a failed line (item: teacher-month item view).
export const retry_confirm_summary = (item) => {
  const i = item || {};
  const to =
    [i.bank_name, masked_last4(i.account_masked)].filter(Boolean).join(' ') || 'the same account';
  return `Sends ${i.amount} again to ${to} in the next bank file.`;
};
